import Link from 'next/link';
import { cn, formatLocationCaseText } from '@/helpers/common';
import { getIndustryTitle, getOccupationTitle, getSeniorityTitle } from '@/helpers/taxonomy';
import { TSearchFilterParam } from './useSearchForm';

type SelectedFiltersProps = {
  locations?: string[];
  occupations?: string[];
  industries?: string[];
  seniorities?: string[];
  getRemoveHref: (param: TSearchFilterParam, code: string) => string;
  classNames?: {
    wrapper?: string;
    label?: string;
    tag?: string;
    removeIcon?: string;
  };
};

type TFilterGroup = {
  param: TSearchFilterParam;
  label: string;
  codes: string[];
  getTitle: (code: string) => string;
};

const SelectedFilters = ({
  locations = [],
  occupations = [],
  industries = [],
  seniorities = [],
  getRemoveHref,
  classNames,
}: SelectedFiltersProps) => {
  const allGroups: TFilterGroup[] = [
    { param: 'l', label: 'LOCATIONS', codes: locations, getTitle: formatLocationCaseText },
    { param: 'occ', label: 'OCCUPATIONS', codes: occupations, getTitle: getOccupationTitle },
    { param: 'ind', label: 'INDUSTRIES', codes: industries, getTitle: getIndustryTitle },
    { param: 'sen', label: 'SENIORITY', codes: seniorities, getTitle: getSeniorityTitle },
  ];
  const groups = allGroups.filter(({ codes }) => codes.length > 0);

  if (groups.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-2 pt-4', classNames?.wrapper)}>
      {groups.map(({ param, label, codes, getTitle }) => (
        <div key={param} className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn('text-xs font-bold', classNames?.label)}>{label}</span>
          {codes.map((code) => {
            const title = getTitle(code) || code;

            return (
              <span
                key={code}
                className={cn(
                  'flex items-center gap-1.5 text-[13px] pl-2 pr-1.5 py-1',
                  classNames?.tag,
                )}
              >
                {title}
                <Link
                  href={getRemoveHref(param, code)}
                  aria-label={`Remove ${title}`}
                  className={cn('flex p-0.5 default-transition', classNames?.removeIcon)}
                >
                  <svg
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                    className="w-2.5 h-2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M1 1l12 12M13 1L1 13" />
                  </svg>
                </Link>
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default SelectedFilters;
